import { useState } from 'react'
import type { CSSProperties, FormEvent } from 'react'
import { URN_AUTH_RATE_LIMITED } from '@clearroute/shared'

import { ApiError, apiPost } from '../lib/api'
import { Spinner } from '../ui/Spinner'

/**
 * SignInScreen — email-only magic-link request UI (UI-SPEC §Components 9).
 *
 * Copy is lifted verbatim from UI-SPEC §Copywriting Contract:
 *   "Sign in to ClearRoute"
 *   "Enter your email and we'll send you a magic link."
 *   "you@example.com" · "Send magic link" · "Sending…"
 *   "Check your email — we sent a link to {email}."
 *   "Too many requests. Please wait a minute and try again."
 *   "Something went wrong. Please try again."
 *
 * Form validation: `^\S+@\S+\.\S+$` loose regex — invalid disables the
 * button (no inline error per UI-SPEC line 362). On submit we POST
 * to /auth/request via apiPost which honours the Problem URN contract.
 * URN_AUTH_RATE_LIMITED surfaces the rate-limited copy; anything else
 * surfaces generic error copy (T-2-JWT: never echo backend detail).
 */

const EMAIL_RE = /^\S+@\S+\.\S+$/

type FormState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; email: string }
  | { kind: 'rate-limited' }
  | { kind: 'error' }

export function SignIn() {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<FormState>({ kind: 'idle' })

  const emailValid = EMAIL_RE.test(email.trim())
  const sending = state.kind === 'sending'
  const disabled = !emailValid || sending

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (disabled) return
    const trimmed = email.trim()
    setState({ kind: 'sending' })
    try {
      await apiPost('/auth/request', { email: trimmed })
      setState({ kind: 'sent', email: trimmed })
    } catch (error) {
      if (error instanceof ApiError && error.urn === URN_AUTH_RATE_LIMITED) {
        setState({ kind: 'rate-limited' })
      } else {
        setState({ kind: 'error' })
      }
    }
  }

  return (
    <main style={shellStyle}>
      <section style={cardStyle} aria-labelledby="signin-heading">
        <h1 id="signin-heading" style={headingStyle}>
          Sign in to ClearRoute
        </h1>
        <p style={bodyStyle}>
          Enter your email and we&rsquo;ll send you a magic link.
        </p>
        <form onSubmit={onSubmit} aria-busy={sending}>
          <label htmlFor="signin-email" style={srOnlyStyle}>
            Email address
          </label>
          <input
            id="signin-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={sending}
            style={inputStyle}
          />
          <button
            type="submit"
            disabled={disabled}
            style={buttonStyle(disabled)}
          >
            {sending ? (
              <span style={buttonLabelWithSpinner}>
                <Spinner size={18} />
                Sending&hellip;
              </span>
            ) : (
              'Send magic link'
            )}
          </button>
        </form>

        {state.kind === 'sent' && (
          <p style={successStyle} role="status">
            Check your email &mdash; we sent a link to {state.email}.
          </p>
        )}
        {state.kind === 'rate-limited' && (
          <p style={errorStyle} role="status">
            Too many requests. Please wait a minute and try again.
          </p>
        )}
        {state.kind === 'error' && (
          <p style={errorStyle} role="status">
            Something went wrong. Please try again.
          </p>
        )}
      </section>
    </main>
  )
}

// --- Styles (inline, token-driven). UI-SPEC §Components 9 dimensions.

const shellStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100dvh',
  padding: 'var(--space-4)',
  background: 'var(--color-secondary)',
}

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 400,
  padding: 'var(--space-6)',
  background: 'var(--color-dominant)',
  borderRadius: 16,
  boxShadow: 'var(--color-shadow)',
}

const headingStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-8)',
  fontSize: 'var(--font-size-heading)',
  fontWeight: 'var(--font-weight-semibold)' as unknown as number,
  lineHeight: 'var(--line-height-heading)' as unknown as number,
  color: 'var(--color-text-primary)',
}

const bodyStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-4)',
  fontSize: 'var(--font-size-body)',
  color: 'var(--color-text-secondary)',
}

const inputStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  padding: '0 var(--space-3)',
  marginBottom: 'var(--space-4)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  background: 'var(--color-dominant)',
  color: 'var(--color-text-primary)',
  fontSize: 'var(--font-size-body)',
}

function buttonStyle(disabled: boolean): CSSProperties {
  return {
    display: 'block',
    width: '100%',
    height: 44,
    borderRadius: 12,
    background: disabled ? 'var(--color-secondary)' : 'var(--color-accent)',
    color: disabled ? 'var(--color-text-secondary)' : '#FFFFFF',
    fontSize: 'var(--font-size-body)',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    border: 0,
    transition: 'background var(--motion-fast) ease',
  }
}

const buttonLabelWithSpinner: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  justifyContent: 'center',
}

const successStyle: CSSProperties = {
  marginTop: 'var(--space-4)',
  marginBottom: 0,
  fontSize: 'var(--font-size-label)',
  color: 'var(--color-text-primary)',
}

const errorStyle: CSSProperties = {
  marginTop: 'var(--space-4)',
  marginBottom: 0,
  fontSize: 'var(--font-size-label)',
  color: 'var(--color-destructive)',
}

const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}
